/**
 * `resegment()` — rebuilding CUES from ASR WORD TIMESTAMPS.
 *
 * THE PREMISE, and it is the whole reason this component exists: **Whisper's segment boundaries
 * are model artefacts, not caption lines.** They fall where the decoder's 30-second window and
 * its punctuation guesses put them, which is routinely mid-clause and occasionally mid-word.
 * Shipping them as captions is the single most visible quality difference between a transcript
 * and a subtitle file, which is why `ResegmentOp.honourSegmentBoundaries` defaults to **false**
 * — encoded as a default rather than as a docs paragraph.
 *
 * WHAT DECIDES A BOUNDARY, in the order the checks are applied:
 *
 *   1. **A silence gap** ≥ `silenceSplit`. A pause is the most reliable boundary signal there
 *      is, because it is measured rather than inferred.
 *   2. **A speaker change**, when `breakOnSpeakerChange`. A two-speaker cue is an UNFIXABLE
 *      caption defect downstream: no later op can recover the boundary, because the
 *      information that there was one is gone. So it is forced here or never.
 *   3. **`maxDuration`** — above it a cue overstays and the reader's eye has nothing to do.
 *   4. **The cell budget**, `maxCells × maxLines`, measured in GRAPHEME CELLS through the
 *      injected `Measure` (full-width = 2). Never `string.length`.
 *   5. **`maxCps`** — the reading-speed ceiling. This one is subtle: splitting does NOT reduce
 *      cps, because each half keeps its own words and its own duration. What it does is cap the
 *      character budget for a given duration, so a dense stretch breaks earlier than the cell
 *      budget alone would ask for.
 *   6. **A segment boundary**, ONLY when `honourSegmentBoundaries` is true.
 *
 * WHAT IS NOT HERE: line breaking. CLEAN owns the breaker and exports `breakOffsets`; this
 * imports it. Two independent line breakers would give the same document different cue splits
 * depending on which path produced it — file or ASR — which is precisely the class of bug the
 * ownership rule exists to prevent.
 *
 * CUE IDS ARE STABLE. `ctx.alloc.cueFor(`${segmentId}:${firstWordIndex}`)` is derived from the
 * key rather than a counter, so nudging `maxCells` from 42 to 41 produces ALIGNED ids and the
 * before/after diff shows edits instead of 100% add/remove churn. That is the entire reason
 * `cueFor` is in the contract.
 */

import type {
  Cue,
  CueKind,
  Diagnostic,
  LossEntry,
  ResegmentOp,
  RunContext,
  SubtitleDocument,
  TextLine,
  TextRun,
  Ticks,
  TranscriptSegment,
  TranscriptWord,
  TransformResult,
} from '../../contracts/cue-model.ts';
import { makeCue, makeLine, makeRun, originFor } from '../core/model/build.ts';
import { sortCues } from '../core/model/lookup.ts';
import { recomputeTextFlags } from '../core/model/text.ts';
import { CHUNK } from '../core/protocol.ts';
import { registerTransform, transforms } from '../core/registry/registries.ts';
import { divRound, T } from '../core/time/ticks.ts';
// CLEAN owns the line breaker. `resegment` IMPORTS it — it does not carry a second one.
import { breakOffsets } from '../transforms/clean/linebreak.ts';

/*───────────────────────────────────────────────────────────────────────────────────────
  Word stream
───────────────────────────────────────────────────────────────────────────────────────*/

interface Word {
  readonly text: string;
  readonly start: number;
  readonly end: number;
  readonly probability: number | null;
  readonly speaker: string | null;
  readonly segmentId: number;
  /** Index of this word WITHIN its segment — the key half of `cueFor`. */
  readonly indexInSegment: number;
}

interface Candidate {
  readonly words: readonly Word[];
  /** True when the candidate's times were derived rather than measured. */
  readonly interpolated: boolean;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  resegment
───────────────────────────────────────────────────────────────────────────────────────*/

export function resegment(
  doc: SubtitleDocument,
  op: ResegmentOp,
  ctx: RunContext,
): TransformResult {
  const diagnostics: Diagnostic[] = [];
  const loss: LossEntry[] = [];

  /*
   * THE ONLY PLACE `signal.cancelled` IS READ, and it is read ONCE (adapter.md 1.2.0,
   * cue-model 1.4.0).
   *
   * `Transform` is synchronous by signature — it returns `TransformResult`, not a Promise — so
   * the worker cannot return to its event loop while this runs, so a queued `cancel` is never
   * delivered, so the flag CANNOT CHANGE between the first statement here and the last. This
   * file used to poll it every `CHUNK.CUES` words, following the contract's earlier wording;
   * that read one value up to forty times and was dead code. Three instances wrote the same
   * loop and the contract now says so in both places.
   *
   * Reading it once IS a genuine early-out: the pipeline runner drains the queue between ops,
   * so a cancel pressed before this op started is honoured here. Anything finer would be
   * theatre, and the next person would read it as cancellation that works.
   */
  if (ctx.signal?.cancelled === true) {
    diagnostics.push({
      code: 'invalid-op-parameters',
      severity: 'info',
      phase: 'transform',
      scope: {},
      message: 'resegment was canceled before it started; the document is unchanged',
    });
    return { doc, touched: [], diagnostics, loss, maxRoundingTicks: 0 };
  }

  const transcript = doc.transcripts.find((t) => t.trackId === op.trackId);
  if (transcript === undefined) {
    // `runPipeline` validates this before dispatching, so reaching it means resegment was
    // called directly. It still returns the document UNCHANGED plus an error rather than
    // throwing, because that is the rule every op follows.
    return {
      doc,
      touched: [],
      diagnostics: [
        {
          code: 'invalid-op-parameters',
          severity: 'error',
          phase: 'transform',
          scope: { trackId: op.trackId },
          message: `no transcript owns track ${op.trackId}; resegment cannot replace cues it has no source for`,
        },
      ],
      loss: [],
      maxRoundingTicks: 0,
    };
  }

  /*── 1. the word stream, with hallucinated segments dropped ──────────────────*/

  const words: Word[] = [];
  let droppedSegments = 0;
  let interpolatedSegments = 0;

  for (const segment of transcript.segments) {
    if (isHallucination(segment, op.dropAboveNoSpeechProb)) {
      droppedSegments++;
      diagnostics.push({
        code: 'hallucination-suspected',
        severity: 'info',
        phase: 'transcribe',
        scope: {},
        message:
          `segment ${segment.id} was dropped: noSpeechProb ${segment.noSpeechProb?.toFixed(3)} ` +
          `exceeds ${op.dropAboveNoSpeechProb}. Silence hallucination is the model's most common failure.`,
      });
      continue;
    }
    if (segment.words !== null && segment.words.length > 0) {
      segment.words.forEach((word, i) => {
        words.push(fromWord(word, segment, i));
      });
      continue;
    }
    // NO WORD TIMESTAMPS. The fallback is the segment itself — measured nothing below the
    // segment, so every time it produces is an estimate and says so.
    const text = segment.text.replace(/^\s+/, '');
    if (text === '') continue;
    interpolatedSegments++;
    words.push({
      // A pseudo-word gets the same leading-space convention a real one has, so two segments
      // concatenated do not run together as 'prose.A second'. `textOf` left-trims the first.
      text: words.length === 0 ? text : ` ${text}`,
      start: segment.time.start as number,
      end: segment.time.end as number,
      probability: null,
      speaker: segment.speaker,
      segmentId: segment.id,
      indexInSegment: 0,
    });
  }

  if (words.length === 0) {
    const survivors = doc.cues.filter((c) => c.trackId !== op.trackId);
    return {
      doc: survivors.length === doc.cues.length ? doc : { ...doc, cues: survivors },
      touched: doc.cues.filter((c) => c.trackId === op.trackId).map((c) => c.id),
      diagnostics,
      loss,
      maxRoundingTicks: 0,
    };
  }

  /*── 2. boundaries ───────────────────────────────────────────────────────────*/

  const candidates = groupIntoCandidates(words, op, ctx, interpolatedSegments > 0);

  /*── 3. cues ─────────────────────────────────────────────────────────────────*/

  const built: Cue[] = [];
  const styleId = doc.styles.defaultId;
  let maxRoundingTicks = 0;

  // Cues whose times came from segment boundaries rather than measured words (see applySpacing).
  const derived = new Set<Cue['id']>();
  candidates.forEach((candidate, index) => {
    const cue = buildCue(candidate, index, op, ctx, doc, transcript.inputId, styleId);
    if (cue === null) return;
    built.push(cue);
    if (candidate.interpolated) derived.add(cue.id);
  });

  // Padding, minimum duration and minimum gap, applied as a NEIGHBOUR pass so a cue can be
  // extended only into space the next one is not using.
  const spaced = applySpacing(built, op, derived);
  for (const cue of spaced) {
    const source = built.find((c) => c.id === cue.id);
    if (source === undefined) continue;
    maxRoundingTicks = Math.max(
      maxRoundingTicks,
      Math.abs((cue.time.end as number) - (source.time.end as number)),
    );
  }

  /*── 4. replace exactly this track's cues ────────────────────────────────────*/

  const replaced = doc.cues.filter((c) => c.trackId === op.trackId);
  const others = doc.cues.filter((c) => c.trackId !== op.trackId);
  const next: SubtitleDocument = {
    ...doc,
    cues: [],
    meta: { ...doc.meta, producedBy: 'transform' },
  };
  const cues = sortCues(next, [...others, ...spaced]);

  if (interpolatedSegments > 0) {
    loss.push({
      code: 'timing-interpolated',
      severity: 'warning',
      target: null,
      scope: { trackId: op.trackId },
      count: interpolatedSegments,
      recoverable: true,
      detail:
        `${interpolatedSegments} segment(s) carried no word timestamps, so their cue times were ` +
        'derived from segment boundaries and grapheme proportion rather than measured',
    });
  }
  if (droppedSegments > 0) {
    loss.push({
      code: 'comment-dropped',
      severity: 'info',
      target: null,
      scope: { trackId: op.trackId },
      count: droppedSegments,
      recoverable: true,
      detail: `${droppedSegments} segment(s) dropped as suspected silence hallucination`,
    });
  }

  return {
    doc: { ...next, cues },
    touched: [...replaced.map((c) => c.id), ...spaced.map((c) => c.id)],
    diagnostics,
    loss,
    maxRoundingTicks,
  };
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Hallucination filter
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * `noSpeechProb` above the threshold — and the threshold DEFAULTS TO `null`, so this is off unless a
 * caller asks for it. See `./op.ts` for why.
 *
 * THE FRAMING HERE WAS WRONG UNTIL S24, and it is worth correcting rather than deleting because the
 * wrong version is the one a reader will reconstruct. This used to say *"the standard Whisper filter
 * is three conditions (`avgLogprob < -1.0 || compressionRatio > 2.4 || noSpeechProb > 0.6`) and
 * `ResegmentOp` exposes only the third"*. Read at the source — the library is installed at
 * `.venv/Lib/site-packages/faster_whisper/transcribe.py` — the three scalars are **three mechanisms
 * with three different jobs**, not one filter with three clauses:
 *
 *   DISCARD, the segment is never emitted        :1216-1224
 *       no_speech_prob > 0.6  AND  NOT (avg_logprob > -1.0)
 *   RETRY HOTTER, re-decode, discards nothing    :1481-1498
 *       compression_ratio > 2.4   →  needs_fallback = True   # too repetitive
 *       avg_logprob < -1.0        →  needs_fallback = True
 *   STOP RETRYING, accept as silence             :1507-1513
 *       the same conjunction again, used to PREVENT a fallback
 *
 * So `compressionRatio` never discards anything, and `avgLogprob` appears in two mechanisms — which
 * is what made flattening them into one `||` so tempting. The rule that reaches us is the DISCARD,
 * and it is a **two-field conjunction**; `ResegmentOp` exposes one of those two, which is why the
 * default is off rather than 0.6.
 *
 * A repetition loop can survive every temperature retry and arrive here undiscarded, so a
 * consumer-side guard on `compressionRatio` is still defensible — but it needs its own justification
 * rather than inheriting this filter's, and it is not implemented.
 */
function isHallucination(segment: TranscriptSegment, threshold: number | null): boolean {
  if (threshold === null) return false;
  return segment.noSpeechProb !== null && segment.noSpeechProb > threshold;
}

function fromWord(word: TranscriptWord, segment: TranscriptSegment, index: number): Word {
  return {
    // The word EXACTLY as the provider emitted it, LEADING WHITESPACE PRESERVED. faster-whisper
    // emits ' So', ' today' — and 'row'/'-level' with NO space on a hyphenated continuation, so
    // the reconstruction rule is CONCATENATION and never join(' '). Stripping here would break
    // every space-free script outright and split hyphenated English visibly.
    text: word.text,
    start: word.time.start as number,
    end: word.time.end as number,
    probability: word.probability,
    speaker: word.speaker ?? segment.speaker,
    segmentId: segment.id,
    indexInSegment: index,
  };
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Boundary selection
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * Groups the word stream into cue candidates.
 *
 * THE WIDTHS ARE ACCUMULATED, NOT RE-MEASURED. Measuring the whole candidate on every word looks
 * harmless and is the defect the contract's own §13 warns about from the other direction: each
 * accumulated string is DISTINCT, so the `Measure` memo never hits and every word costs a cold
 * `Intl.Segmenter` scan — the 82 ms per 5,000 words that is a 1–1.6 s freeze on a phone. It is
 * also quadratic in string allocation. Adding the new word's cells to a running total is one
 * measurement per WORD-SHAPE, which the memo then serves from cache.
 *
 * The approximation: cell width is summed per word rather than measured across the join. That is
 * exact unless a word BEGINS with a combining mark, which would attach to the previous word's
 * last cluster — degenerate in a word stream, and the error is at most one cell.
 */
function groupIntoCandidates(
  words: readonly Word[],
  op: ResegmentOp,
  ctx: RunContext,
  anyInterpolated: boolean,
): readonly Candidate[] {
  const out: Candidate[] = [];
  let current: Word[] = [];
  const cellBudget = op.break.maxCells * op.break.maxLines;
  const cpsFloor = op.break.maxCells * CPS_BREAK_MIN_FILL;

  /** Running totals for the candidate being accumulated. */
  let cells = 0;
  let clusters = 0;

  const flush = (): void => {
    if (current.length === 0) return;
    out.push({ words: current, interpolated: anyInterpolated });
    current = [];
    cells = 0;
    clusters = 0;
  };

  for (let i = 0; i < words.length; i++) {
    /*
     * PROGRESS, which is what `CHUNK.CUES` is for — not cancellation.
     *
     * `onProgress` is a `postMessage`: it queues on the MAIN thread's loop and does not need the
     * worker to yield, so it reaches the user from inside a long synchronous op where
     * `signal.cancelled` cannot. That asymmetry is the whole of adapter.md 1.2.0. A
     * re-segmentation of a feature-length transcript is exactly the case it exists for: the user
     * cannot stop it short of terminating the worker, so they must at least be able to watch it.
     */
    if (i % CHUNK.CUES === 0) ctx.onProgress?.(i, words.length, 'resegment');

    const word = words[i] as Word;
    const previous = current[current.length - 1];
    const wordCells = ctx.measure.measure(word.text);
    const wordClusters = ctx.measure.measure(word.text.replace(/\s+/gu, ''));

    if (previous !== undefined) {
      const first = current[0] as Word;
      const duration = word.end - first.start;
      const nextCells = cells + wordCells;
      const nextClusters = clusters + wordClusters;

      if (word.start - previous.end >= (op.silenceSplit as number)) {
        flush();
      } else if (op.breakOnSpeakerChange && word.speaker !== previous.speaker) {
        flush();
      } else if (op.honourSegmentBoundaries && word.segmentId !== previous.segmentId) {
        flush();
      } else if (duration > (op.maxDuration as number)) {
        flush();
      } else if (nextCells > cellBudget) {
        flush();
      } else if (
        /*
         * The reading-speed ceiling, as a character budget for the duration in hand.
         *
         * IT IS A SOFT SIGNAL, and the `cpsFloor` guard is why. When a SINGLE word already
         * exceeds the ceiling — 'apparently' in a fifth of a second — breaking cannot fix it:
         * each half keeps its own words and its own duration, so cps does not fall. A naive
         * check then breaks before the dense word and again after it, isolating it as a
         * one-word cue, and repeats down the whole passage. That is worse than an over-speed
         * cue: it is over-speed AND unreadable.
         *
         * So a cps break requires the candidate to already hold half a line — the smallest
         * amount that reads as a caption rather than a flash. `cpsFloor` is a tuning weight,
         * not a user threshold: the ceiling itself is `maxCps`, and a cue that stays over it is
         * reported by the QC pass rather than mangled here.
         */
        op.maxCps > 0 &&
        duration > 0 &&
        nextCells >= cpsFloor &&
        nextClusters / (duration / 120_000) > op.maxCps
      ) {
        flush();
      }
    }

    current.push(word);
    cells += wordCells;
    clusters += wordClusters;
  }
  flush();
  return out;
}

/** How full a candidate must be, as a fraction of one line, before the cps ceiling may break
 *  it. Below this a split produces a flash rather than a caption and does not reduce cps. */
const CPS_BREAK_MIN_FILL = 0.5;

/** THE reconstruction rule: concatenation, then ONE left-trim when a cue is built. */
function textOf(words: readonly Word[]): string {
  let out = '';
  for (const word of words) out += word.text;
  return out.replace(/^\s+/, '');
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Cue construction
───────────────────────────────────────────────────────────────────────────────────────*/

function buildCue(
  candidate: Candidate,
  index: number,
  op: ResegmentOp,
  ctx: RunContext,
  doc: SubtitleDocument,
  inputId: SubtitleDocument['inputs'][number]['id'],
  styleId: SubtitleDocument['styles']['defaultId'],
): Cue | null {
  const words = candidate.words;
  const first = words[0] as Word;
  const last = words[words.length - 1] as Word;
  const text = textOf(words);
  if (text === '') return null;

  // CLEAN's breaker. One implementation, so a sentence breaks in the same place whether it
  // arrived from a file or from ASR.
  const broken = breakOffsets(text, op.break, ctx.measure);

  // STABLE IDS: derived from the first word's `${segmentId}:${wordIndex}`, so re-running with
  // nudged options diffs as edits rather than churn.
  const id = ctx.alloc.cueFor(`${first.segmentId}:${first.indexInSegment}`);

  const lines: TextLine[] = [];
  const segmentIds = new Set<number>();
  for (const word of words) segmentIds.add(word.segmentId);

  if (op.emitWordRuns && !candidate.interpolated) {
    // One run per word, `timeKind: 'word'` — the differentiator switch. From here, ASS `\k`,
    // Enhanced LRC and word-level VTT all work with no extra step.
    const runsByLine = sliceWordsToLines(words, text, broken.lines, ctx);
    runsByLine.forEach((runs, i) => {
      lines.push(makeLine(runs, { breakBefore: i === 0 ? 'none' : 'reflowed' }));
    });
  } else {
    broken.lines.forEach((line, i) => {
      lines.push(
        makeLine(
          line.text === ''
            ? []
            : [
                makeRun({
                  id: ctx.alloc.run(),
                  text: line.text,
                  ...(candidate.interpolated ? {} : { confidence: averageProbability(words) }),
                }),
              ],
          { breakBefore: i === 0 ? 'none' : 'reflowed' },
        ),
      );
    });
  }

  const speaker = first.speaker;
  const base = makeCue({
    id,
    time: { start: T.ticks(first.start), end: T.ticks(Math.max(first.start + 1, last.end)) },
    lines: lines.length === 0 ? [makeLine([])] : lines,
    styleId,
    trackId: op.trackId,
    origin: {
      ...originFor(inputId, 'resegment', index),
      segmentIds: [...segmentIds].sort((a, b) => a - b),
      wordRange: {
        segmentId: first.segmentId,
        from: first.indexInSegment,
        to: last.segmentId === first.segmentId ? last.indexInSegment : first.indexInSegment,
      },
    },
    granularity: op.emitWordRuns && !candidate.interpolated ? 'word' : 'coalesced',
    flags: {
      forced: false,
      autoGenerated: true,
      locked: false,
      hasFragileTags: false,
      hasRelativeTimes: op.emitWordRuns && !candidate.interpolated,
    },
  });

  const cue: Cue = {
    ...base,
    kind: op.classifyKinds ? classifyKind(text) : 'dialogue',
    actor: op.breakOnSpeakerChange ? speaker : null,
  };
  void doc;
  return { ...cue, flags: recomputeTextFlags(cue, cue.lines) };
}

/**
 * Maps the broken lines back onto the word runs.
 *
 * The break points are CHARACTER offsets into the concatenated text, and the words are the
 * timing atoms, so a break that lands mid-word would destroy a measured timing. It cannot:
 * every break candidate is at a cluster boundary that follows whitespace or a CJK character,
 * and word boundaries are exactly those places. A break that somehow lands inside a word keeps
 * the word whole on the earlier line, which is the conservative direction.
 */
function sliceWordsToLines(
  words: readonly Word[],
  text: string,
  lines: readonly { readonly from: number; readonly to: number }[],
  ctx: RunContext,
): readonly (readonly TextRun[])[] {
  // Character offsets of each word in `text`, accounting for the single left-trim.
  const trimmed = words.length > 0 ? (words[0] as Word).text.replace(/^\s+/, '') : '';
  const spans: { word: Word; from: number; to: number; text: string }[] = [];
  let at = 0;
  words.forEach((word, i) => {
    const body = i === 0 ? trimmed : word.text;
    spans.push({ word, from: at, to: at + body.length, text: body });
    at += body.length;
  });

  const out: TextRun[][] = [];
  for (const line of lines) {
    const runs: TextRun[] = [];
    for (const span of spans) {
      // A word belongs to the line containing its first NON-WHITESPACE character.
      //
      // Not its first character: the breaker's line ranges EXCLUDE the whitespace around a
      // break, so a word whose leading space straddles the boundary has its space in neither
      // range. Keying on the first character drops that word entirely — one word missing from
      // the middle of a caption, which is the kind of defect that survives review because the
      // sentence still reads.
      const contentAt = span.from + (span.text.length - span.text.replace(/^\s+/u, '').length);
      if (contentAt < line.from || contentAt >= line.to) continue;
      // A line boundary eats the whitespace between words, so the leading space of the first
      // word on a continuation line is dropped rather than rendered.
      const body = runs.length === 0 ? span.text.replace(/^\s+/, '') : span.text;
      if (body === '') continue;
      runs.push(
        makeRun({
          id: ctx.alloc.run(),
          text: body,
          time: {
            start: T.ticks(span.word.start),
            end: T.ticks(Math.max(span.word.start + 1, span.word.end)),
          },
          timeKind: 'word',
          timeOrigin: 'asr',
          confidence: span.word.probability,
          // NOT always true. faster-whisper splits 'row-level' into 'row' + '-level' and
          // 'didn't' into 'did' + "n't", with NO leading space on the continuation — those are
          // one word in two runs, which is exactly what `startsWord` exists to record. Getting
          // it wrong makes WPM, word-level correction and every writer's joiner decision wrong,
          // and it is not derivable from whitespace alone because CJK has no spaces and every
          // character is a word.
          startsWord: startsWordAt(span.word.text, runs.length === 0 && out.length === 0),
        }),
      );
    }
    out.push(runs);
  }
  void text;
  return out.filter((runs, i) => runs.length > 0 || i === 0);
}

/** Leading whitespace, the first word of the cue, or a CJK character — all word starts. */
function startsWordAt(text: string, isFirst: boolean): boolean {
  if (isFirst) return true;
  if (/^\s/u.test(text)) return true;
  const cp = text.codePointAt(0) ?? 0;
  return (
    (cp >= 0x3000 && cp <= 0x9fff) ||
    (cp >= 0xac00 && cp <= 0xd7ff) ||
    (cp >= 0xff00 && cp <= 0xffef) ||
    (cp >= 0x20000 && cp <= 0x3ffff)
  );
}

function averageProbability(words: readonly Word[]): number | null {
  const values = words.map((w) => w.probability).filter((p): p is number => p !== null);
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/**
 * `CueKind` from the text, so `removeSdh` works on the transcription path. Whisper routinely
 * emits '[Music]', '(laughs)' and '♪' — classifying once here is the difference between a
 * cleaning pass that finds them and one that does not.
 */
function classifyKind(text: string): CueKind {
  const t = text.trim();
  if (t === '') return 'dialogue';
  if (/^[♪♫]/.test(t) || /[♪♫]$/.test(t)) return 'music';
  if (/^[[(（【]\s*(music|musique|música|музыка|音乐|音楽)\s*[\])）】]$/i.test(t)) return 'music';
  if (/^[[(（【][^\])）】]*[\])）】]$/s.test(t)) return 'sound';
  return 'dialogue';
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Padding, minimum duration, minimum gap
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * ASR onsets run late and its offsets run early, so the padding is asymmetric — a lead-in of
 * ~60 ms and a lead-out of ~120 ms are the values the contract names.
 *
 * Every extension is bounded by the NEIGHBOUR: a cue never grows into the next one's space, and
 * `minGap` is preserved so a renderer blinks between adjacent cues rather than running them
 * together. The conservative direction is to leave a gap, never to create an overlap.
 */
function applySpacing(
  cues: readonly Cue[],
  op: ResegmentOp,
  derived: ReadonlySet<Cue['id']> = new Set(),
): readonly Cue[] {
  const padStart = op.padStart as number;
  const padEnd = op.padEnd as number;
  const minGap = op.minGap as number;
  const minDuration = op.minDuration as number;

  const out: Cue[] = [];
  for (let i = 0; i < cues.length; i++) {
    const cue = cues[i] as Cue;
    const previous = out[out.length - 1];

    /*
     * PADDING IS NEGOTIABLE; CONTENT IS NOT.
     *
     * The neighbour bounds may take the padding away, and they may NEVER cut into the span the
     * words actually occupy. `contentStart`/`contentEnd` are that span, and every clamp below
     * stops there — because a cue whose end sits before its last word's end violates the
     * document invariant that run times are contained in `[cue.start, cue.end]`, and it makes a
     * karaoke export outrun its own cue.
     */
    const contentStart = cue.time.start as number;
    /*
     * A DERIVED END IS NOT CONTENT. With no word timestamps (every caption-route transcript)
     * a cue's end is its last segment's end, and for YouTube's automatic captions that is when
     * the line scrolled OFF screen, not when it stopped being spoken: each line stays up while
     * the next one arrives. A founder's transcript (2026-10-17) had all 454 boundaries
     * overlapping, by 2.2s at the median, so every SRT, VTT, ASS and SBV made from it showed two
     * subtitles at once, stacked, in any player. Such a cue may end where the next one starts.
     * Its runs carry no times, so no run can be left outside it. It still never ends at or before
     * its own start: two lines that start together keep the overlap rather than become 0s cues.
     */
    const next = cues[i + 1];
    const derivedCeiling =
      derived.has(cue.id) && next !== undefined
        ? (next.time.start as number) - minGap
        : Number.POSITIVE_INFINITY;
    const contentEnd =
      derivedCeiling > contentStart
        ? Math.min(cue.time.end as number, derivedCeiling)
        : (cue.time.end as number);

    let start = contentStart - padStart;
    let end = contentEnd + padEnd;

    // `prev` is the ALREADY-UPDATED previous cue, which is what makes the cascade exist at all.
    if (previous !== undefined) {
      const floor = (previous.time.end as number) + minGap;
      if (start < floor) start = Math.min(floor, contentStart);
    }
    if (start < 0) start = 0;

    // `next` is the ORIGINAL next cue: a not-yet-visited cue has no updated value to read, and
    // stopping where it ACTUALLY is can leave a gap but can never create an overlap.
    const ceiling =
      next === undefined ? Number.POSITIVE_INFINITY : (next.time.start as number) - minGap;
    if (end > ceiling) end = Math.max(ceiling, contentEnd);

    if (end - start < minDuration) {
      const wanted = start + minDuration;
      end = Math.max(end, Math.min(wanted, Math.max(ceiling, contentEnd)));
    }
    if (end <= start) end = Math.max(start + 1, contentEnd);

    out.push(
      (start as number) === (cue.time.start as number) &&
        (end as number) === (cue.time.end as number)
        ? cue
        : { ...cue, rev: cue.rev + 1, time: { start: T.ticks(start), end: T.ticks(end) } },
    );
  }
  return out;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  SELF-REGISTRATION (S7)

  `resegment` is CONVERT's op. CORE wired it in its registration barrel because leaving a built
  op unreachable to protect an ownership boundary would have been repeating F5 on purpose — and
  flagged it at the call site so it would not become permanent by silence. Taking it back, here.

  THERE ARE TWO CALL PATHS AND EACH HAS A DISTINCT REASON. Both are needed; neither is belt to
  the other's braces:

  1. **The top-level call below** is the self-registration proper. A bare
     `import '../resegment/index.ts'` now registers, so CORE's barrel can delete its import and
     its guarded `registerTransform` line and nothing breaks.

  2. **`registerFormats()` also calls `registerResegment()`.** A module's top-level side effect
     runs ONCE per process. A test that calls `resetRegistries()` and then re-registers would
     otherwise find the op gone with no way to get it back, because re-importing a loaded module
     is a no-op. That is not a hypothetical: `installFormats()` in `test/convert/_fixtures/ctx.ts`
     does exactly that, and so does CORE's own registration smoke harness.

  The guard means the two paths cannot collide: the registry refuses a duplicate with a DIFFERENT
  value and accepts the same one, and `has()` short-circuits before it gets that far.
───────────────────────────────────────────────────────────────────────────────────────*/

/** Registers `resegment` as the `'resegment'` transform. Idempotent. */
export function registerResegment(): void {
  if (transforms.has('resegment')) return;
  registerTransform('resegment', resegment);
}

registerResegment();

export type { Ticks };
export { divRound };

/*───────────────────────────────────────────────────────────────────────────────────────
  THE DEFAULT OP, RE-EXPORTED — and the reason this block exists at all.

  `resegmentOp(trackId)` has existed since S7 and fills all fifteen fields from the constants CLEAN
  escalated. It was in `./op.ts`, which this barrel did not re-export and nothing outside
  `test/convert` imported — so UI looked at the barrel, found `resegment()` and nothing that builds
  its op, wrote `throw new ResegmentDefaultsUnavailable()`, and the Download button has been disabled
  since. The CTO then grepped for `DEFAULT_RESEGMENT` and `resegmentDefaults` and also found nothing.

  A complete default set existed the whole time and was unreachable from where anyone looked. That is
  the third time in three rounds: the emoji branch was unreachable by ORDERING, the capability rows
  were unreachable CHEAPLY by module placement, and this was unreachable by not being exported. All
  three shipped as "correct in isolation" and none is visible to a grep for the thing you want.

  `trackId` is why this is a function and not a constant: it names which track's cues are REPLACED, so
  it cannot be defaulted. `language` is optional and worth passing — it selects the CJK cell budget.
───────────────────────────────────────────────────────────────────────────────────────*/

export type { ResegmentOverrides } from './op.ts';
export { breakOptionsFor, RESEGMENT_DEFAULTS, resegmentOp } from './op.ts';
